import { apiTemplates } from "@/api/template.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ApiTemplateSavePayload } from "@/types/template.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const templatesKeys = createQueryKeys("templates", {
  all: null,
  preview: (id: number | undefined, candidateId: number) => ({
    queryKey: [id, candidateId],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Id is required");

      const result = await apiTemplates.preview(id, candidateId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useTemplates = (enabled = true) => {
  return useQuery({
    ...templatesKeys.all,
    queryFn: async () => {
      const result = await apiTemplates.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};

export const useTemplateSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number | undefined;
      payload: ApiTemplateSavePayload;
    }) => {
      const result = id
        ? await apiTemplates.update(id, payload)
        : await apiTemplates.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: templatesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTemplateDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiTemplates.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: templatesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTemplatePreview = (id: number | undefined, candidateId: number) => {
  return useQuery({
    ...templatesKeys.preview(id, candidateId),
    enabled: !!id,
  });
};
